import { gql } from '@apollo/client'
import { Modal, VisuallyHidden } from '@farol-ds/react'
import classNames from 'classnames'
import { useCallback, useEffect, useState } from 'react'

import { StudentEnrollmentValidation_ViewerFragment } from './__generated__/student-enrollment-validation.graphql'
import { StudentEnrollmentValidationContextProvider } from './context'
import { EnrollmentCloseTracker } from './enrollment-close-tracker'
import { Steps } from './enums'
import { StepRenderer } from './step-renderer'
import styles from './student-enrollment-validation.module.scss'
import {
  StudentEnrollmentValidationCallback,
  StudentEnrollmentValidationEventData,
  StudentEnrollmentValidationTexts,
} from './types'

interface StudentEnrollmentValidationProps {
  viewer: StudentEnrollmentValidation_ViewerFragment | null
  isOpen: boolean
  eventData: StudentEnrollmentValidationEventData
  validateCallback?: StudentEnrollmentValidationCallback
  texts?: StudentEnrollmentValidationTexts
  onOpenChange?: (isOpen: boolean) => void
  validationOrigin: string
  onAfterValidationSuccess?: () => void | Promise<void>
}

export function StudentEnrollmentValidation(props: StudentEnrollmentValidationProps) {
  const {
    texts,
    isOpen,
    eventData,
    viewer,
    validateCallback,
    onOpenChange,
    validationOrigin,
    onAfterValidationSuccess,
  } = props

  const [currentStep, setCurrentStep] = useState<Steps>(Steps.ACADEMIC_DATA)

  const onClose = useCallback(() => {
    onOpenChange?.(false)
  }, [onOpenChange])

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(Steps.ACADEMIC_DATA)
    }
  }, [isOpen])

  if (!isOpen || !viewer) {
    return null
  }

  const containerClassName = classNames({
    [styles.studentModalContainer]:
      currentStep === Steps.ACADEMIC_DATA ||
      currentStep === Steps.UPLOAD_INSTRUCTIONS ||
      currentStep === Steps.DOCUMENT_PREVIEW,
  })

  return (
    <Modal open={isOpen} onOpenChange={onOpenChange}>
      <VisuallyHidden>
        <Modal.Header>
          <Modal.HeaderTitle>Student Enrollment Validation</Modal.HeaderTitle>
        </Modal.Header>
      </VisuallyHidden>
      <Modal.Content className={styles.modalContent} containerClassName={containerClassName}>
        <StudentEnrollmentValidationContextProvider
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          eventData={eventData}
          viewer={viewer}
          validationOrigin={validationOrigin}
          texts={texts}
          onAfterValidationSuccess={onAfterValidationSuccess}
        >
          <EnrollmentCloseTracker />
          <Modal.IconClose onClick={onClose} data-testid="close-button" />
          <StepRenderer
            onClose={onClose}
            currentStep={currentStep}
            stepsCallback={validateCallback}
          />
        </StudentEnrollmentValidationContextProvider>
      </Modal.Content>
    </Modal>
  )
}

StudentEnrollmentValidation.fragments = {
  viewer: gql`
    fragment StudentEnrollmentValidation_viewer on Profile {
      pid
      name
      cpfName
    }
  `,
}
